import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Loader2, Sheet, Save, PlugZap, DatabaseBackup, ExternalLink, CheckCircle2, AlertTriangle, Copy } from 'lucide-react';
import api from '../utils/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' }) : '—');

export const GoogleSheetsSyncCard = () => {
  const [cfg, setCfg] = useState(null);
  const [spreadsheet, setSpreadsheet] = useState('');
  const [saJson, setSaJson] = useState('');
  const [backfillFrom, setBackfillFrom] = useState('2026-10-01');
  const [rebuildDate, setRebuildDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/integrations/google-sheets');
      setCfg(res.data);
      setSpreadsheet(res.data.spreadsheet_id || '');
      setBackfillFrom(res.data.backfill_from || '2026-10-01');
    } catch { toast.error('Could not load Google Sheets settings'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (extra = {}) => {
    setBusy('save');
    try {
      const body = { spreadsheet_id: spreadsheet, backfill_from: backfillFrom, ...extra };
      if (saJson.trim()) body.service_account_json = saJson.trim();
      await api.put('/admin/integrations/google-sheets', body);
      setSaJson('');
      toast.success('Google Sheets settings saved');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setBusy(''); }
  };

  const toggle = async (enabled) => {
    setBusy('toggle');
    try {
      await api.put('/admin/integrations/google-sheets', { enabled });
      toast.success(enabled ? 'Real-time sync ON' : 'Sync paused');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
    finally { setBusy(''); }
  };

  const test = async () => {
    setBusy('test');
    try {
      const res = await api.post('/admin/integrations/google-sheets/test');
      toast.success(`Connected to "${res.data.title}" (${res.data.tabs.length} tab${res.data.tabs.length === 1 ? '' : 's'})`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Connection failed'); }
    finally { setBusy(''); }
  };

  const backfill = async () => {
    setBusy('backfill');
    try {
      const res = await api.post('/admin/integrations/google-sheets/backfill');
      toast.success(`Backfill since ${res.data.since}: ${res.data.synced} synced, ${res.data.failed} failed`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Backfill failed'); }
    finally { setBusy(''); }
  };

  const rebuild = async () => {
    if (!window.confirm(`Rebuild tab for ${rebuildDate}? The tab is deleted and re-created from the app's records. Any manual edits on that tab (e.g. Invoice No) will be lost.`)) return;
    setBusy('rebuild');
    try {
      const res = await api.post('/admin/integrations/google-sheets/rebuild-day', { date: rebuildDate });
      toast.success(`Tab ${res.data.tab} rebuilt: ${res.data.synced} rows`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rebuild failed'); }
    finally { setBusy(''); }
  };

  const rebuildAll = async () => {
    if (!window.confirm(`Rebuild EVERY day's tab from ${backfillFrom} to today? All tabs are deleted and re-created from the app's records. Manual edits (e.g. Invoice No) on those tabs will be lost.`)) return;
    setBusy('rebuildAll');
    try {
      const res = await api.post('/admin/integrations/google-sheets/rebuild-all');
      toast.success(`Full rebuild started from ${res.data.since}`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not start rebuild'); }
    finally { setBusy(''); }
  };

  const prog = cfg?.rebuild_progress;
  useEffect(() => {
    if (!prog?.running) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [prog?.running, load]);

  if (!cfg) return null;
  const ready = cfg.has_key && cfg.spreadsheet_id;
  const pending = cfg.pending_count || 0;

  return (
    <Card className="p-6 rounded-2xl" data-testid="sheets-sync-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Sheet className="w-5 h-5 text-emerald-700" /></div>
          <div>
            <h2 className="text-base md:text-lg font-semibold text-slate-900">Google Sheets — Receipt Register</h2>
            <p className="text-xs text-slate-500">One tab per day (DD-MM-YYYY), one row per payment method. Cancelled receipts are removed from the day's tab automatically.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${cfg.enabled ? 'text-emerald-700' : 'text-slate-500'}`} data-testid="sheets-sync-status">{cfg.enabled ? 'Live' : 'Paused'}</span>
          <Switch checked={cfg.enabled} disabled={!ready || busy === 'toggle'} onCheckedChange={toggle} data-testid="sheets-sync-toggle" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat label="Synced" value={cfg.synced_count} testId="sheets-stat-synced" />
        <Stat label="Pending" value={pending} tone={pending ? 'text-amber-700' : ''} testId="sheets-stat-pending" />
        <Stat label="Errors" value={cfg.error_count} tone={cfg.error_count ? 'text-rose-700' : ''} testId="sheets-stat-errors" />
        <Stat label="Last sync" value={fmt(cfg.last_sync_at)} small testId="sheets-stat-last" />
      </div>
      {cfg.last_error && (
        <div className="mb-4 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="sheets-last-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> Last error: {cfg.last_error}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <Label className="text-sm">Spreadsheet URL or ID</Label>
          <div className="flex gap-2 mt-1">
            <Input value={spreadsheet} onChange={(e) => setSpreadsheet(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" className="rounded-xl" data-testid="sheets-spreadsheet-input" />
            {cfg.spreadsheet_url && (
              <Button variant="outline" className="rounded-xl" onClick={() => window.open(cfg.spreadsheet_url, '_blank')} data-testid="sheets-open-btn"><ExternalLink className="w-4 h-4" /></Button>
            )}
          </div>
        </div>
        <div>
          <Label className="text-sm">Service account JSON key</Label>
          <Textarea
            value={saJson}
            onChange={(e) => setSaJson(e.target.value)}
            placeholder={cfg.has_key ? 'Key saved. Paste a new JSON only to replace it.' : 'Paste the full JSON key file content here'}
            className="mt-1 rounded-xl font-mono text-xs min-h-[90px]"
            data-testid="sheets-sa-json-input"
          />
          {cfg.service_account_email && (
            <p className="text-xs text-slate-600 mt-1 flex items-center gap-1 flex-wrap" data-testid="sheets-sa-email">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Share the spreadsheet (Editor) with
              <code className="bg-slate-100 px-1 rounded">{cfg.service_account_email}</code>
              <button type="button" className="text-emerald-700 hover:underline inline-flex items-center gap-0.5" onClick={() => { navigator.clipboard.writeText(cfg.service_account_email); toast.success('Copied'); }} data-testid="sheets-copy-email-btn"><Copy className="w-3 h-3" /> copy</button>
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-sm">Backfill receipts from</Label>
            <Input type="date" value={backfillFrom} onChange={(e) => setBackfillFrom(e.target.value)} className="mt-1 rounded-xl w-44" data-testid="sheets-backfill-from-input" />
          </div>
          <Button onClick={() => save()} disabled={!!busy} className="rounded-xl" data-testid="sheets-save-btn">
            {busy === 'save' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
          </Button>
          <Button variant="outline" onClick={test} disabled={!!busy || !ready} className="rounded-xl" data-testid="sheets-test-btn">
            {busy === 'test' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />} Test connection
          </Button>
          <Button variant="outline" onClick={backfill} disabled={!!busy || !cfg.enabled} className="rounded-xl" data-testid="sheets-backfill-btn">
            {busy === 'backfill' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DatabaseBackup className="w-4 h-4 mr-2" />} Sync pending ({pending})
          </Button>
        </div>
        <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-slate-100">
          <div>
            <Label className="text-sm">Rebuild a day's tab (if deleted or edited by mistake)</Label>
            <Input type="date" value={rebuildDate} onChange={(e) => setRebuildDate(e.target.value)} className="mt-1 rounded-xl w-44" data-testid="sheets-rebuild-date-input" />
          </div>
          <Button variant="outline" onClick={rebuild} disabled={!!busy || !cfg.enabled || prog?.running} className="rounded-xl text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="sheets-rebuild-btn">
            {busy === 'rebuild' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DatabaseBackup className="w-4 h-4 mr-2" />} Rebuild tab
          </Button>
          <Button onClick={rebuildAll} disabled={!!busy || !cfg.enabled || prog?.running} className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white" data-testid="sheets-rebuild-all-btn">
            {busy === 'rebuildAll' || prog?.running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DatabaseBackup className="w-4 h-4 mr-2" />}
            {prog?.running ? `Rebuilding ${prog.done + prog.failed}/${prog.total}${prog.current ? ` · ${prog.current}` : ''}` : `Rebuild all from ${backfillFrom}`}
          </Button>
        </div>
        {prog && !prog.running && prog.finished_at && (
          <p className="text-xs text-slate-500" data-testid="sheets-rebuild-summary">
            Last full rebuild: {prog.done} day(s) rebuilt{prog.failed ? `, ${prog.failed} failed` : ''}{prog.total ? ` of ${prog.total}` : ''} · {fmt(prog.finished_at)} by {prog.started_by}
            {prog.note && <span className="text-amber-700" data-testid="sheets-rebuild-note"> · {prog.note}</span>}
          </p>
        )}
      </div>
    </Card>
  );
};

const Stat = ({ label, value, tone = '', small, testId }) => (
  <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`${small ? 'text-sm' : 'text-xl'} font-semibold text-slate-900 ${tone}`}>{value ?? 0}</div>
  </div>
);

export default GoogleSheetsSyncCard;
